import {MutableRefObject, useCallback, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useHistory, useLocation, useParams, useRouteMatch} from 'react-router-dom';
import {translations} from '../../../../../../i18n/translations';
import {AffiliatedUser} from '../../../../../../backend/responses/affiliated-user';
import {UsersRepository} from '../../../../../../repositories/users';
import {UserType} from '../../../../../../backend/enums/user-type';
import {ScreenPaths} from '../../../../../../enums/screen-path';
import {AlertModal} from '../../../../../../components/alert-modal/alert-modal';
import {IndexViewScreen} from '../../../../../../components/index-view-screen/index-view-screen';
import {Button, Stack, Typography} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import SaveIcon from '@mui/icons-material/SaveAlt';
import {SystemEntitiesIndexListCard} from '../../../../../../components/system-entities-index-list-card/system-entities-index-list-card';
import {SystemEntitiesIndexListItem} from '../../../../../../components/system-entities-index-list-item/system-entities-index-list-item';
import {SystemEntitiesCardSkeleton} from '../../../../../../components/system-entities-index-list-card/skeleton';
import {CaregiverPathExtensions} from '../../../../../../components/caregiver-routes/caregiver-routes';
import AddStaffFlow from '../modals/add-staff-flow/renderer';
import {useAuth} from '../../../../../../contexts/auth/context';
import {useExportDataWithErrorHandler} from '../../../../../../hooks/use-export-data-with-error-handler';
import {ScrollFetchParams} from '../../../../../../hooks/infinite-scroll-items';
import {useFetchAllItems} from '../../../../../../hooks/fetch-all-items';
import {useInfinityScrollItems, useObserveViewport} from '../../../../../../hooks';
import {
  PaginatedTableColumn,
  PaginatedTableItem,
} from '../../../../../../interfaces/paginated-table-data';
import {RolesRepository} from '../../../../../../repositories/roles';
import {Role} from '../../../../../../backend/interfaces/role';
import {joinAsExpression} from '../../../../../../utils/functions';
import {RolePermissionType} from '../../../../../../backend/enums/role-permission-type';
import {SystemEntitiesRowSkeleton} from '../../../../../../components/system-entities-index-list-item/skeleton';
import {AffiliationType} from '../../../../../../backend/enums/affiliation-type';
import {Product} from '../../../../../../backend/enums/product';
import {prepareProfessionalCaregiversListPDF} from '../../../../../../utils/data/export-data/professional-caregivers/export-professional-caregivers-pdf';
import {prepareProfessionalCaregiversListXLSX} from '../../../../../../utils/data/export-data/professional-caregivers/export-professional-caregivers-xlsx';
import {exportProfessionalCaregiversGetReportData} from '../../../../../../utils/data/export-data/professional-caregivers/base/export-professional-caregivers-get-report-data';
import {exportProfessionalProcessReportData} from '../../../../../../utils/data/export-data/professional-caregivers/base/export-professional-caregivers-process-report-data';
import {API_PAGINATION_MAX_LIMIT} from '../../../../../../utils/constants/api';

// Columns IDs
export enum ColumnIdentifier {
  Name = 'name',
  Email = 'email',
  AffiliationType = 'affiliation-type',
  State = 'state',
  Roles = 'roles',
}

type ProfessionalCaregiverRow = PaginatedTableItem<AffiliatedUser> & {
  enabled: boolean;
  columns: PaginatedTableColumn[];
};

function getCaregiverSortField(sort?: string) {
  const field = sort?.trim()?.split(/\s+/, 1)[0];

  switch (field) {
    case 'name':
    case 'birthday':
    case 'room':
      return field;
    default:
      return undefined;
  }
}

function sortProfessionalCaregivers(data: ProfessionalCaregiverRow[], sort?: string) {
  const normalizedSort = sort?.trim();
  const field = getCaregiverSortField(sort);

  if (!field || !normalizedSort) {
    return data;
  }

  const [, direction] = normalizedSort.split(/\s+/, 2);
  const sortDirection = direction === 'DESC' ? -1 : 1;

  return [...data].sort((entryA, entryB) => {
    const getComparableValue = (entry: ProfessionalCaregiverRow) => {
      switch (field) {
        case 'name':
          return entry.item.user.displayName ?? entry.item.user.name ?? '';
        case 'birthday':
          return entry.item.user.email ?? '';
        case 'room':
          return entry.item.user.phoneNumber ?? '';
        default:
          return '';
      }
    };

    return (
      getComparableValue(entryA).localeCompare(getComparableValue(entryB), undefined, {
        sensitivity: 'base',
      }) * sortDirection
    );
  });
}

export default function ProfessionalCaregiversProfessionalCaregiverOfficeScreen() {
  const {hasWritePermission, state: authState} = useAuth();
  const canWriteCaregivers = hasWritePermission(RolePermissionType.Team_Professionals_Profile);
  const canDeleteCaregivers = hasWritePermission(RolePermissionType.Team_Professionals);
  let {entityId}: {entityId?: string} = useParams();
  const {t} = useTranslation();
  // The `path` lets us build <Route> paths that are
  // relative to the parent route, while the `url` lets
  // us build relative links.
  let {url, path} = useRouteMatch();
  let history = useHistory();
  const {isRunningMobile} = useObserveViewport();
  const pageSize = isRunningMobile ? 20 : 50;
  const {previewPdfWithErrorHandling, previewXlsxWithErrorHandling} =
    useExportDataWithErrorHandler();

  // Hold state
  const [deletedProfessionalCaregiverName, setDeletedProfessionalCaregiverName] = useState<
    string | null
  >(new URLSearchParams(useLocation().search).get(ScreenPaths.ProfessionalCaregiversDeletedQuery));

  const [onAddStaffFlow, setOnAddStaffFlow] = useState(false);

  const professionalCaregiversCache = useRef<{
    key: string;
    data: ProfessionalCaregiverRow[];
  } | null>(null);
  const pendingProfessionalCaregiversCache = useRef<{
    key: string;
    data: ProfessionalCaregiverRow[];
  } | null>(null);

  const mapProfessionalCaregiverRow = (obj: AffiliatedUser): ProfessionalCaregiverRow => {
    return {
      id: obj.user.id || '',
      item: obj,
      enabled: obj.user.isActive,
      avatarUrl: obj.user.picture,
      toLink:
        url.charAt(url.length - 1) === '/'
          ? `${url}${obj.user.id}/${CaregiverPathExtensions.Profile}`
          : `${url}/${obj.user.id}/${CaregiverPathExtensions.Profile}`,
      columns: [] as PaginatedTableColumn[],
    };
  };

  const mapPendingProfessionalCaregiverRow = (obj: any): ProfessionalCaregiverRow => {
    const item: AffiliatedUser = {
      user: {
        name: obj.data?.destinationName ?? '',
        email: obj.data?.destinationEmail,
        phoneNumber: obj.data?.destinationPhoneNumber,
        isActive: false,
        picture: undefined,
        type: UserType.Caregiver,
        product: Product.Bluebird,
        // @ts-ignore - extremely hacky. This is here because of delete invite logic. This is a mess..
        state: obj.state,
        // extremely hacky. This is here because of delete invite
        id: obj.id,
      },
      affiliation: {
        type: obj.data?.type ?? AffiliationType.Caregiver,
        entityId: obj.data?.targetEntityId ?? '',
        userId: '',
      },
      entitySocialResponses: [],
    };

    return {
      id: obj.id || '',
      item,
      enabled: false,
      avatarUrl: undefined,
      toLink: '',
      columns: [] as PaginatedTableColumn[],
    };
  };

  const getAllProfessionalCaregivers = async (
    search?: string
  ): Promise<ProfessionalCaregiverRow[]> => {
    if (!entityId) throw new Error('Empty entity id.');

    const cacheKey = `${entityId}:${search ?? ''}`;
    if (professionalCaregiversCache.current?.key === cacheKey) {
      return professionalCaregiversCache.current.data;
    }

    const allRows: ProfessionalCaregiverRow[] = [];
    let totalCount = 0;

    do {
      const response = await UsersRepository.instance.getInEntity(
        entityId,
        pageSize,
        allRows.length,
        UserType.Caregiver,
        search,
        true
      );

      totalCount = response.totalCount;
      allRows.push(...response.data.map(mapProfessionalCaregiverRow));
    } while (allRows.length < totalCount);

    professionalCaregiversCache.current = {key: cacheKey, data: allRows};
    return allRows;
  };

  const getAllPendingProfessionalCaregivers = async (
    search?: string
  ): Promise<ProfessionalCaregiverRow[]> => {
    if (!entityId) throw new Error('Empty entity id.');

    const cacheKey = `${entityId}:${search ?? ''}`;
    if (pendingProfessionalCaregiversCache.current?.key === cacheKey) {
      return pendingProfessionalCaregiversCache.current.data;
    }

    const allRows: ProfessionalCaregiverRow[] = [];
    let totalCount = 0;

    do {
      const response = await UsersRepository.instance.getPendingInEntity(
        entityId,
        API_PAGINATION_MAX_LIMIT,
        allRows.length,
        'destinationName',
        'in-progress',
        search,
        true
      );

      totalCount = response.totalCount;
      allRows.push(...response.data.map(mapPendingProfessionalCaregiverRow));
    } while (allRows.length < totalCount);

    pendingProfessionalCaregiversCache.current = {key: cacheKey, data: allRows};
    return allRows;
  };

  const handleGetAllProfessionalCaregivers = async ({
    limit = pageSize,
    skip = 0,
    search,
    sort,
  }: ScrollFetchParams) => {
    if (!entityId) throw new Error('Empty entity id.');

    if (getCaregiverSortField(sort)) {
      const [professionalCaregivers, pendingProfessionalCaregivers] = await Promise.all([
        getAllProfessionalCaregivers(search),
        getAllPendingProfessionalCaregivers(search),
      ]);

      const sortedData = sortProfessionalCaregivers(
        [...professionalCaregivers, ...pendingProfessionalCaregivers],
        sort
      );

      return {
        data: sortedData.slice(skip, skip + limit).map((row) => ({...row, columns: []})),
        totalCount: sortedData.length,
      };
    }

    const {data, totalCount} = await handleGetProfessionalCaregivers(limit, skip, search);
    const pendingLimit = data.length < limit ? limit - data.length : 1;
    const pendingSkip = totalCount < skip ? skip - totalCount : 0;
    const {pendingData, pendingCount} = await handleGetPendingProfessionalCaregivers(
      pendingLimit,
      pendingSkip,
      search
    );

    if (data.length < limit) data.push(...pendingData);

    return {data, totalCount: totalCount + pendingCount};
  };

  const handleGetProfessionalCaregivers = async (
    limit: number,
    skip: number,
    search?: string
  ): Promise<{data: ProfessionalCaregiverRow[]; totalCount: number}> => {
    if (!entityId) throw new Error('Empty entity id.');

    const {totalCount, data}: {totalCount: number; data: Array<AffiliatedUser>} =
      await UsersRepository.instance.getInEntity(
        entityId,
        limit,
        skip,
        UserType.Caregiver,
        search,
        true
      );

    const processedData: ProfessionalCaregiverRow[] = data.map(mapProfessionalCaregiverRow);
    // Return result
    return {
      data: processedData,
      totalCount,
    };
  };

  const handleGetPendingProfessionalCaregivers = async (
    limit: number,
    skip: number,
    search?: string
  ): Promise<{pendingData: ProfessionalCaregiverRow[]; pendingCount: number}> => {
    // Load caregivers
    const result = await UsersRepository.instance.getPendingInEntity(
      entityId!,
      limit,
      skip,
      'destinationName',
      'in-progress',
      search,
      true
    );

    // Process data
    const processedData: ProfessionalCaregiverRow[] = result.data.map(
      mapPendingProfessionalCaregiverRow
    );

    // Return result
    return {
      pendingData: processedData,
      pendingCount: result.totalCount,
    };
  };

  // Handle close dialog
  const handleCloseDialog = () => {
    // Reset state
    setDeletedProfessionalCaregiverName(null);

    // Reset history
    history.replace(path.replace(':entityId', entityId || ''));
  };

  const toggleAddStaffFlow = () => setOnAddStaffFlow((state) => !state);

  const loaderRef = useRef() as MutableRefObject<HTMLButtonElement>;

  const {items: roles, isLoading: rolesLoading} = useFetchAllItems({
    fetchFunction: ({limit, skip}) => {
      return RolesRepository.instance.getRoles(entityId!, limit, skip);
    },
  });

  const rolesTransformer = useCallback(
    (data: PaginatedTableItem<AffiliatedUser>[]) => {
      data.forEach((user) => {
        if (!roles) return;
        const userRoles = roles
          ?.filter((role) => user.item.affiliation.rolesIds?.includes(role.id!))
          .map((role: Role) => role.name);

        const userRolesExpression = joinAsExpression(userRoles);

        user.columns.push({
          columnId: ColumnIdentifier.Roles,
          value: userRolesExpression,
        });
      });
    },
    [roles]
  );

  const dataScrollInstance = useInfinityScrollItems<PaginatedTableItem<AffiliatedUser>>({
    fetchData: handleGetAllProfessionalCaregivers,
    initialSort: 'name ASC',
    pageSize,
    loaderRef,
    dataTransformer: rolesTransformer,
    enabled: !rolesLoading,
  });

  const handleTriggerRefetch = () => {
    professionalCaregiversCache.current = null;
    pendingProfessionalCaregiversCache.current = null;
    dataScrollInstance.triggerRefetch();
  };

  return (
    <>
      <IndexViewScreen
        pageHeader={{
          screenTitle: t(translations.professional_caregivers.root.title),
        }}
        searchOptions={{
          searchString: true,
          toggleDisplay: true,
        }}
        dataScrollInstance={dataScrollInstance}
        emptyStateMessage={{
          title: t(translations.professional_caregivers.root.no_professional_caregivers),
          subtitle: t(translations.index_grid_screen.empty_message_add_encouraging, {
            type: t(translations.professional_caregivers.root.colaborator),
          }),
        }}
        rightColumn={({resultsLength}) => (
          <Stack gap={2} alignSelf="flex-end" alignItems="flex-end">
            {resultsLength ? (
              <Typography
                sx={{maxWidth: '100%', paddingRight: '34px'}}
                fontSize="14px"
                fontWeight={600}
                variant="caption"
                color="white"
                noWrap
              >
                {t(translations.index_grid_screen.total_counter)}: {resultsLength}{' '}
                {t(
                  resultsLength === 1
                    ? translations.professional_caregivers.root.colaborator
                    : translations.professional_caregivers.root.colaborators
                )}
              </Typography>
            ) : null}
            {canWriteCaregivers && (
              <Button
                size="large"
                variant="inverted"
                startIcon={<AddIcon />}
                onClick={toggleAddStaffFlow}
                sx={{
                  minWidth: 'unset',
                  width: isRunningMobile ? '60px' : '146px',
                  height: '40px',
                  '.MuiButton-startIcon': {
                    margin: {xs: 'inherit', md: 0},
                  },
                }}
              >
                {!isRunningMobile && t(translations.index_grid_screen.add_button)}
              </Button>
            )}
          </Stack>
        )}
        renderItem={{
          grid: {
            element: SystemEntitiesIndexListCard,
            skeleton: SystemEntitiesCardSkeleton,
            gridItemWidth: 200,
          },
          list: {
            element: SystemEntitiesIndexListItem,
            skeleton: SystemEntitiesRowSkeleton,
          },
        }}
        actions={{
          actions: [
            {
              label: t(translations.office.generics.save_pdf),
              icon: {element: SaveIcon},
              callback: async () => {
                await previewPdfWithErrorHandling(async () => {
                  const entity = authState.session?.selectedAffiliation?.entity;
                  const baseReportData = await exportProfessionalCaregiversGetReportData({
                    entity,
                  });
                  if (!baseReportData) {
                    return null;
                  }

                  const reportData = exportProfessionalProcessReportData({
                    reportData: baseReportData,
                  });

                  const pdf = prepareProfessionalCaregiversListPDF({
                    reportData,
                    entity,
                    t,
                  });

                  return {
                    file: pdf.file,
                    fileName: pdf.fileName,
                  };
                });
              },
            },
            {
              label: t(translations.office.generics.save_xlsx),
              icon: {element: SaveIcon},
              callback: async () => {
                await previewXlsxWithErrorHandling(async () => {
                  const entity = authState.session?.selectedAffiliation?.entity;
                  const baseReportData = await exportProfessionalCaregiversGetReportData({
                    entity,
                  });
                  if (!baseReportData) {
                    return null;
                  }

                  const reportData = exportProfessionalProcessReportData({
                    reportData: baseReportData,
                  });

                  const xlsx = prepareProfessionalCaregiversListXLSX({
                    reportData,
                    entity,
                    t,
                  });

                  return {
                    file: xlsx.file,
                    fileName: xlsx.fileName,
                  };
                });
              },
            },
          ],
        }}
        canWrite={canWriteCaregivers}
        canDelete={canDeleteCaregivers}
        loaderRef={loaderRef}
        viewType={UserType.Caregiver}
      />

      <AddStaffFlow
        triggerRefetch={handleTriggerRefetch}
        inFlow={onAddStaffFlow}
        onClose={toggleAddStaffFlow}
      />

      <AlertModal
        id="professional-caregiver-deleted-warning"
        opened={deletedProfessionalCaregiverName ? true : false}
        title={t(
          translations.professional_caregivers.root.deleted_professional_caregiver_dialog_title,
          {
            name: deletedProfessionalCaregiverName,
          }
        )}
        content={t(
          translations.professional_caregivers.root.deleted_professional_caregiver_dialog_message
        )}
        handleClose={handleCloseDialog}
        actions={[
          {
            label: t(
              translations.professional_caregivers.root
                .deleted_professional_caregiver_dialog_ok_button
            ),
            type: 'neutral',
            handleClick: handleCloseDialog,
          },
        ]}
      />
    </>
  );
}
